import { Link } from "react-router";
import { docsPath } from "./docsNav";

const CLIENT_EXAMPLES = [
  {
    title: "Connect with verified TLS and Bearer authentication",
    code: `use woven_client::{Client, ClientConfig, ClientTlsConfig};
use woven_protocol::AuthenticationScheme;

let tls = ClientTlsConfig::from_ca_pem(ca_pem.as_bytes())?;
let client = Client::connect_with_tls_and_auth(
    ClientConfig {
        url: format!("quic://{endpoint}"),
        token,
        ..ClientConfig::default()
    },
    tls,
    AuthenticationScheme::Bearer,
)
.await?;`,
  },
  {
    title: "Request managed admission",
    code: `use std::time::Duration;
use tokio::sync::oneshot;

let (cancel_tx, cancel_rx) = oneshot::channel::<()>();
// Keep cancel_tx with your application's stop control.
let (mut client, outcome) = client
    .admit_with_cancellation(
        namespace_id,
        session_id,
        "quickstart-connection".to_owned(),
        Duration::from_secs(30),
        async move { let _ = cancel_rx.await; },
    )
    .await?;

// Continue only after AdmissionStatus::Admitted or QueueState::Admitted.`,
  },
  {
    title: "Subscribe and publish an Ephemeral event",
    code: `client
    .subscribe_space(namespace_id, session_id, space_id, space_epoch, 1)
    .await?;

// Receive SubscriptionAccepted and EntityEntered, then use the assigned entity ID.
client
    .publish_event(
        namespace_id,
        session_id,
        space_id,
        space_epoch,
        1,          // managed Ephemeral channel
        entity_id,  // assigned by WOVEN
        sequence,
        type_id,
        payload,
    )
    .await?;`,
  },
] as const;

const STEPS = [
  {
    title: "1. Create a product",
    body: "Sign in after access approval and open Create Server. Choose a product type, tick rate and CCU allocation. Capacity = Hz × CCU; the approved Lite default budget is 250 units (25 CCU at 10 Hz). Stay within the remaining capacity the console reports.",
  },
  {
    title: "2. Wait for Running",
    body: "Connect after the product reaches Running. The reveal action supplies the endpoint and credential for the managed client.",
  },
  {
    title: "3. Reveal the connection descriptor",
    body: "Use the Running product's reveal action. Reauthenticate if prompted, keep the descriptor out of source control, URLs, and logs, and expose it only to the intended client runtime.",
  },
  {
    title: "4. Add a Spatial Subspace (optional)",
    body: "On the server Details page, add an immutable 1–64 character slug and display name. The defaults create a bounded 3D space with 1 meter per unit, 10-unit cells, a 25-unit interest radius, exact-distance filtering, and the documented AABB. Host allocates its numeric ID from 3.",
  },
  {
    title: "5. Connect a client",
    body: "Use the Rust woven-client with the descriptor's native QUIC endpoint or the TypeScript browser client with webTransport.url. Keep TLS verification enabled, present the returned Bearer credential for managed admission, and consume logical and spatial definitions exactly as returned.",
  },
  {
    title: "6. Publish an Ephemeral event",
    body: "Use the exact managed namespace, session, space definition, and channel returned by the descriptor. Managed channels carry live Ephemeral events for current subscribers.",
  },
] as const;

export function DocsQuickstartPage() {
  return (
    <div className="docs-article">
      <p className="docs-eyebrow">Getting started</p>
      <h1 className="docs-page-title">Quickstart</h1>
      <p className="docs-lede">
        Create a product, optionally add a bounded 3D Spatial Subspace, reveal its managed
        connection descriptor, and use native Rust QUIC or the TypeScript browser client to exchange
        Ephemeral events.
      </p>

      <div className="docs-quickstart-note">
        <span className="badge badge--mock">Lite</span>
        <span>
          250 capacity units by default after approval · native QUIC · browser WebTransport ·
          managed 3D routing
        </span>
      </div>

      {STEPS.map((step) => (
        <section key={step.title}>
          <h2 className="docs-heading">{step.title}</h2>
          <p className="docs-body">{step.body}</p>
        </section>
      ))}

      <h2 className="docs-heading">Client examples</h2>
      <p className="docs-body">
        These abbreviated examples use the real Rust client API. Parse the endpoint, token, numeric
        scope IDs, spaces, and channels from the revealed descriptor rather than hardcoding them.
      </p>
      {CLIENT_EXAMPLES.map((example) => (
        <div className="docs-example" key={example.title}>
          <p className="docs-example-title">{example.title}</p>
          <div className="code-panel">
            <pre className="code-block">
              <code>{example.code}</code>
            </pre>
          </div>
        </div>
      ))}
      <p className="docs-body">
        For complete admission-result handling, subscription responses, entity assignment, TLS
        options, cancellation, and shutdown, see the{" "}
        <Link to={docsPath("sdks/rust")} className="docs-inline-link">
          Rust client guide
        </Link>{" "}
        or the{" "}
        <Link to={docsPath("sdks/javascript")} className="docs-inline-link">
          TypeScript client guide
        </Link>
        .
      </p>

      <h2 className="docs-heading">Managed connection requirements</h2>
      <dl className="detail-list">
        <div className="detail-row">
          <dt>Transport</dt>
          <dd>
            Rust clients use native QUIC. The TypeScript browser client uses WVN1 over WebTransport
            and HTTP/3/QUIC through a separate endpoint on the same managed runtime. Both paths are
            Online.
          </dd>
        </div>
        <div className="detail-row">
          <dt>Security</dt>
          <dd>
            Verify TLS using the trust information supplied for the managed endpoint and send the
            descriptor's credential as Bearer authentication. Do not disable certificate
            verification.
          </dd>
        </div>
        <div className="detail-row">
          <dt>Admission</dt>
          <dd>
            The server validates the managed credential and admits the client to its assigned
            namespace and session. Product IDs and protocol scope IDs are distinct.
          </dd>
        </div>
        <div className="detail-row">
          <dt>Routing and data</dt>
          <dd>
            Use only the logical or managed 3D spatial definitions and channels returned by the
            descriptor. Host does not grant wildcard or ad-hoc spaces. Only Ephemeral events are
            active on this managed path.
          </dd>
        </div>
      </dl>

      <h2 className="docs-heading">Where to go next</h2>
      <div className="docs-topic-grid">
        <Link to={docsPath("core-concepts")} className="panel docs-topic-card">
          <p className="docs-topic-title">Core concepts</p>
          <p className="docs-topic-body">
            Understand products, CCU, descriptors, managed admission, spaces, channels, and events.
          </p>
        </Link>
        <Link to={docsPath("sdks/rust")} className="panel docs-topic-card">
          <p className="docs-topic-title">Rust client</p>
          <p className="docs-topic-body">
            Add the current-source native client as a pinned path dependency and follow the
            validated managed flow.
          </p>
        </Link>
        <Link to={docsPath("sdks/javascript")} className="panel docs-topic-card">
          <p className="docs-topic-title">TypeScript client</p>
          <p className="docs-topic-body">
            Use the Online Host browser descriptor flow, bounded admission, and exact bigint scope
            IDs over WebTransport.
          </p>
        </Link>
        <Link to={docsPath("authentication")} className="panel docs-topic-card">
          <p className="docs-topic-title">Authentication</p>
          <p className="docs-topic-body">
            Keep console identity and the revealed runtime Bearer credential in their separate
            roles.
          </p>
        </Link>
        <Link to={docsPath("events")} className="panel docs-topic-card">
          <p className="docs-topic-title">Events</p>
          <p className="docs-topic-body">
            Learn the event and delivery vocabulary behind the active Ephemeral channel.
          </p>
        </Link>
      </div>

      <h2 className="docs-heading">Availability boundaries</h2>
      <p className="docs-body">
        Native QUIC, browser WebTransport, the TypeScript browser client, Spatial Subspaces, and
        managed spatial routing are Online. Pro and Dedicated, persistence beyond Ephemeral, and
        Inference remain Preview. Integrations use only the exact managed scope documented above.
      </p>
      <Link to={docsPath("deploy/woven-host")} className="docs-inline-link">
        Read about Woven Host deployment boundaries &rarr;
      </Link>
    </div>
  );
}
